import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Camera, ArrowUpRight, Star, Plus } from "lucide-react";
import { FEATURED_WORK } from "@/lib/constants";

const INITIAL_VISIBLE = 10;

const FILTERS = [
    { label: "All", match: () => true },
    { label: "FMCG", match: (w) => w.tag === "FMCG" },
    {
        label: "Web",
        match: (w) => /website|e-commerce|digital/i.test(w.category || ""),
    },
    {
        label: "Activations",
        match: (w) =>
            w.tag === "Activation" ||
            /activation|interactive|ar experience|experiential|event/i.test(
                w.category || ""
            ),
    },
    {
        label: "Apps",
        match: (w) => w.tag === "App" || /\bapp\b/i.test(w.category || ""),
    },
];

// Visually distinct, branded placeholder tiles for entries without imagery.
const GRADIENTS = [
    "from-ae-orange to-ae-orange-light",
    "from-ae-ink to-ae-gray-700",
    "from-ae-orange-deep to-ae-orange",
    "from-ae-ink via-ae-orange-deep to-ae-orange",
    "from-ae-orange-light to-ae-orange",
    "from-ae-gray-700 to-ae-ink",
    "from-ae-orange to-ae-orange-deep",
    "from-ae-ink to-ae-orange-deep",
];

function getInitials(name) {
    return name
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2);
}

export default function Work() {
    const [expanded, setExpanded] = useState(false);
    const [activeFilter, setActiveFilter] = useState("All");
    const matcher = FILTERS.find((f) => f.label === activeFilter).match;
    const filtered = FEATURED_WORK.filter(matcher);
    const featured = filtered.filter((w) => w.featured);
    const rest = filtered.filter((w) => !w.featured);
    const visibleRest = expanded ? rest : rest.slice(0, INITIAL_VISIBLE);
    const hiddenCount = rest.length - INITIAL_VISIBLE;

    return (
        <section
            id="work"
            data-testid="work-section"
            className="relative py-24 md:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
                    <div>
                        <div className="inline-flex items-center gap-2 text-ae-orange text-xs font-bold uppercase tracking-[0.22em]">
                            <Camera className="h-4 w-4" /> Featured Work
                        </div>
                        <h2 className="mt-5 font-display font-extrabold text-4xl md:text-5xl lg:text-6xl text-ae-ink tracking-tight leading-[1.02]">
                            Project snapshots that
                            <br />
                            <span className="text-ae-orange">moved markets</span>.
                        </h2>
                    </div>
                    <a
                        href="#contact"
                        data-testid="work-cta-full-portfolio"
                        className="inline-flex items-center gap-2 self-start md:self-end text-sm font-semibold text-ae-ink hover:text-ae-orange transition-colors"
                    >
                        Request full portfolio
                        <ArrowUpRight className="h-4 w-4" />
                    </a>
                </div>

                {/* Category filter tabs */}
                <div
                    className="flex flex-wrap items-center gap-2.5 mb-10"
                    data-testid="work-filter-tabs"
                >
                    {FILTERS.map((f) => (
                        <button
                            key={f.label}
                            type="button"
                            onClick={() => {
                                setActiveFilter(f.label);
                                setExpanded(false);
                            }}
                            data-testid={`work-filter-${f.label.toLowerCase()}`}
                            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                                activeFilter === f.label
                                    ? "bg-ae-orange text-white shadow-ae-lift"
                                    : "bg-white text-ae-gray-700 border border-black/10 hover:border-ae-orange/50 hover:text-ae-orange"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                {/* Featured (highlighted, image-driven) */}
                {featured.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 mb-6">
                        {featured.map((w, i) => (
                            <FeaturedCard key={w.slug} item={w} index={i} />
                        ))}
                    </div>
                )}

                {/* Rest of portfolio */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                    {visibleRest.map((w, i) => (
                        <motion.div
                            key={w.slug || w.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.7,
                                delay: (i % INITIAL_VISIBLE) * 0.05,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            data-testid={`work-card-${i + featured.length}`}
                            className="group relative rounded-3xl overflow-hidden aspect-square cursor-pointer"
                        >
                            {w.image ? (
                                <>
                                    <img
                                        src={w.image}
                                        alt={`${w.client} — ${w.title}`}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1200ms] ease-out"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ae-ink via-ae-ink/35 to-transparent" />
                                </>
                            ) : (
                                <>
                                    <div
                                        className={`absolute inset-0 bg-gradient-to-br ${GRADIENTS[(i + featured.length) % GRADIENTS.length]}`}
                                    />
                                    <div className="absolute inset-0 mix-blend-overlay opacity-30 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.5),transparent_40%)]" />
                                    <div className="absolute inset-0 ae-noise opacity-30" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="font-display font-black text-white/15 text-[10rem] leading-none select-none">
                                            {getInitials(w.client)}
                                        </span>
                                    </div>
                                </>
                            )}
                            <div className="absolute inset-x-0 top-0 p-5 flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/85 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
                                    {w.tag}
                                </span>
                                <span className="h-9 w-9 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ArrowUpRight className="h-4 w-4" />
                                </span>
                            </div>
                            <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                                <div className="text-[11px] uppercase tracking-[0.2em] text-white/75 font-semibold">
                                    {w.category}
                                </div>
                                <div className="mt-2 font-display font-bold text-xl md:text-2xl text-white leading-tight">
                                    {w.title}
                                </div>
                                <div className="mt-1 text-sm text-white/80">
                                    {w.client}
                                </div>
                            </div>
                            <div className="absolute inset-0 ring-0 group-hover:ring-2 ring-white/30 transition-all rounded-3xl" />
                        </motion.div>
                    ))}
                </div>

                {/* View more / view less toggle */}
                {hiddenCount > 0 && (
                    <div className="mt-10 flex justify-center">
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            data-testid="work-view-more-btn"
                            className="group inline-flex items-center gap-2.5 rounded-full bg-ae-ink px-7 py-3.5 text-sm font-semibold text-white shadow-ae-lift transition-all hover:bg-ae-orange hover:text-ae-ink"
                        >
                            {expanded ? (
                                "Show less"
                            ) : (
                                <>
                                    View more
                                    <span className="text-white/70 group-hover:text-ae-ink/70">
                                        ({hiddenCount} more)
                                    </span>
                                    <Plus className="h-4 w-4 transition-transform group-hover:rotate-90" />
                                </>
                            )}
                        </button>
                    </div>
                )}

                <p
                    className="mt-10 text-center text-sm text-ae-gray-500"
                    data-testid="work-placeholder-note"
                >
                    A selection of recent campaigns, platforms and activations. Full
                    portfolio available on request.
                </p>
            </div>
        </section>
    );
}

function FeaturedCard({ item, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
                duration: 0.85,
                delay: index * 0.08,
                ease: [0.22, 1, 0.36, 1],
            }}
            data-testid={`work-featured-${index}`}
        >
            <Link
                to={`/work/${item.slug}`}
                data-testid={`work-featured-link-${item.slug}`}
                className="group relative block rounded-3xl overflow-hidden aspect-[16/10] shadow-ae-lift ring-1 ring-ae-orange/30 hover:ring-2 hover:ring-ae-orange transition-all"
            >
                <img
                    src={item.image}
                    alt={`${item.client} — ${item.title}`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms] ease-out"
                />
                {/* Bottom gradient for legibility */}
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ae-ink via-ae-ink/60 to-transparent" />

                {/* Top chips */}
                <div className="absolute inset-x-0 top-0 p-5 md:p-6 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-ae-ink bg-ae-orange px-3 py-1.5 rounded-full shadow-ae-soft">
                            <Star className="h-3 w-3 fill-current" />
                            Featured
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white bg-black/35 backdrop-blur-sm px-3 py-1.5 rounded-full">
                            {item.tag}
                        </span>
                    </div>
                    <span className="h-10 w-10 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all group-hover:bg-ae-orange">
                        <ArrowUpRight className="h-5 w-5 group-hover:rotate-45 transition-transform" />
                    </span>
                </div>

                {/* Bottom content */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-white/80 font-semibold">
                        {item.category}
                    </div>
                    <div className="mt-2 font-display font-extrabold text-2xl md:text-3xl text-white leading-tight">
                        {item.title}
                    </div>
                    <div className="mt-1 text-sm md:text-base text-white/85">
                        {item.client}
                    </div>
                    <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-ae-orange-light group-hover:text-white transition-colors">
                        View case study
                        <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform" />
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
